// Lightweight markdown renderer — no dependencies
// Handles: headings (#, ##, ###), bold (**), italic (*), inline code (`),
// code blocks (```), bullet lists (-, *), numbered lists, horizontal rules, line breaks

import React from 'react';

const MarkdownRenderer = ({ content, className = '' }) => {
  if (!content) return null;

  const renderInline = (text) => {
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Bold **text**
      const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*/s);
      // Italic *text* (not **)
      const italicMatch = remaining.match(/^(.*?)(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/s);
      // Inline code `text`
      const codeMatch = remaining.match(/^(.*?)`([^`]+)`/s);

      const matches = [
        boldMatch && { match: boldMatch, type: 'bold', index: boldMatch[1].length },
        italicMatch && { match: italicMatch, type: 'italic', index: italicMatch[1].length },
        codeMatch && { match: codeMatch, type: 'code', index: codeMatch[1].length },
      ].filter(Boolean);

      if (matches.length === 0) {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }

      // Pick the earliest match
      const earliest = matches.reduce((a, b) => a.index <= b.index ? a : b);
      const { match, type } = earliest;

      if (match[1]) parts.push(<span key={key++}>{match[1]}</span>);

      if (type === 'bold') {
        parts.push(<strong key={key++} className="font-semibold">{match[2]}</strong>);
        remaining = remaining.slice(match[1].length + match[2].length + 4);
      } else if (type === 'italic') {
        parts.push(<em key={key++} className="italic">{match[2]}</em>);
        remaining = remaining.slice(match[1].length + match[2].length + 2);
      } else if (type === 'code') {
        parts.push(
          <code key={key++} className="px-1 py-0.5 bg-slate-100 text-slate-800 rounded text-xs font-mono">
            {match[2]}
          </code>
        );
        remaining = remaining.slice(match[1].length + match[2].length + 2);
      }
    }

    return parts;
  };

  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block ```
    if (line.trim().startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={key++} className="my-2 p-3 bg-slate-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
          {codeLines.join('\n')}
        </pre>
      );
      i++;
      continue;
    }

    // Heading ###
    const h3 = line.match(/^###\s+(.+)/);
    if (h3) {
      elements.push(<h3 key={key++} className="text-sm font-bold mt-3 mb-1 text-slate-800">{renderInline(h3[1])}</h3>);
      i++; continue;
    }

    // Heading ##
    const h2 = line.match(/^##\s+(.+)/);
    if (h2) {
      elements.push(<h2 key={key++} className="text-sm font-bold mt-3 mb-1 text-slate-800">{renderInline(h2[1])}</h2>);
      i++; continue;
    }

    // Heading #
    const h1 = line.match(/^#\s+(.+)/);
    if (h1) {
      elements.push(<h2 key={key++} className="text-base font-bold mt-3 mb-1 text-slate-800">{renderInline(h1[1])}</h2>);
      i++; continue;
    }

    // Horizontal rule
    if (line.match(/^(\*\*\*|---|___)\s*$/)) {
      elements.push(<hr key={key++} className="my-3 border-slate-200" />);
      i++; continue;
    }

    // Bullet list item - or *
    if (line.match(/^[\-\*]\s+/)) {
      const listItems = [];
      while (i < lines.length && lines[i].match(/^[\-\*]\s+/)) {
        const itemText = lines[i].replace(/^[\-\*]\s+/, '');
        listItems.push(<li key={i}>{renderInline(itemText)}</li>);
        i++;
      }
      elements.push(
        <ul key={key++} className="my-1.5 ml-4 space-y-0.5 list-disc list-outside text-sm">
          {listItems}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (line.match(/^\d+\.\s+/)) {
      const listItems = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+/)) {
        const itemText = lines[i].replace(/^\d+\.\s+/, '');
        listItems.push(<li key={i}>{renderInline(itemText)}</li>);
        i++;
      }
      elements.push(
        <ol key={key++} className="my-1.5 ml-4 space-y-0.5 list-decimal list-outside text-sm">
          {listItems}
        </ol>
      );
      continue;
    }

    // Empty line — spacing
    if (line.trim() === '') {
      elements.push(<div key={key++} className="h-2" />);
      i++; continue;
    }

    // Regular paragraph
    elements.push(
      <p key={key++} className="text-sm leading-relaxed">
        {renderInline(line)}
      </p>
    );
    i++;
  }

  return <div className={className}>{elements}</div>;
};

export default MarkdownRenderer;